import React, { FC, useContext, useState } from 'react';

import { getI18nData } from '@lucid/utils';
import { graphql, useStaticQuery, withPrefix } from 'gatsby';
import { Box, Heading, Text, LayoutContext, Container } from '@lucid/components-react';
import ReactHtmlParser from 'react-html-parser';
import { keyframes } from '@emotion/react';
import { Drupal_ParagraphNineteenHeroImage } from 'generated/drupal-graphql';
import { mqMin, LucidTheme } from '@lucid/theme';

import ABTestBranch from 'components/ab-test-branch/ab-test-branch';
import ABTest, { Variation } from 'components/ab-test/ab-test';
import NineteenAction from 'components/ab-tests/nineteen-action-custom-google/nineteen-action';
import { RemoteWorkImage } from 'components/ab-tests/remote-work-message-home-page/remote-work-message-home-page';

interface NineteenHeroImageProps {
  entity: Drupal_ParagraphNineteenHeroImage;
}

const nineteenHeroPlayButton = keyframes({
  '0%': {
    transform: 'scale(0)'
  },
  '60%': {
    transform: 'scale(1.5)'
  },
  '100%': {
    transform: 'scale(1)'
  }
});

const NineteenHeroImage: FC<NineteenHeroImageProps> = ({
  entity: {
    fieldHeader,
    fieldHeaderPiece,
    fieldSubheader,
    fieldImage,
    fieldVideoLink,
    fieldAutoplay,
    fieldVideoInitialLoop,
    fieldNineteenAction
  }
}) => {
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_abtest_hph1_heading_4
              }
            }
          }
        }
      }
    `
  );

  const {
    video,
    language: { i18nKey, langPrefix },
    location: { pathname }
  } = useContext(LayoutContext);

  const i18n = getI18nData(i18nData, i18nKey);

  const [isEnded, setIsEnded] = useState(!fieldVideoInitialLoop);

  const getImageHtml = () =>
    fieldVideoInitialLoop ? (
      <video
        autoPlay
        muted
        playsInline
        css={(theme: LucidTheme) => ({
          border: `1px solid ${theme.colors.coolGrey[5]}`,
          borderRadius: theme.radii.card,
          maxWidth: '100%'
        })}
        onEnded={() => {
          setIsEnded(true);
        }}
      >
        <source src={fieldVideoInitialLoop} />
      </video>
    ) :
        (
          <img
            alt={fieldHeader!}
            css={(theme: LucidTheme) => ({
              border: `1px solid ${theme.colors.coolGrey[5]}`,
              borderRadius: theme.radii.card,
              maxWidth: '100%'
            })}
            src={fieldImage!}
          />
        );

  const hasHeaderPieces = fieldHeaderPiece && fieldHeaderPiece.length > 0;

  return (
    <ABTest
      aboveTheFold
      conditionalTrigger={
        langPrefix === 'en' &&
        (pathname === withPrefix('/') || pathname === withPrefix('/20vc'))
      }
      test="Y20Q2_RemoteWorkMessageHomePage"
      trigger={'load'}
    >
      <ABTestBranch branch={Variation.A}>
        <Box
          as="header"
          css={(theme: LucidTheme) => ({
            background: theme.colors.white,
            position: 'relative'
          })}
          py={{ default: 5, tablet: 7 }}
        >
          <Container displayFlex css={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box
              flex={{ default: '0 1 100%', tablet: '0 1 50%' }}
            >
              <Heading variant="headline3XL">
                {hasHeaderPieces
                  ? ReactHtmlParser(fieldHeaderPiece!.join('<br/>'))
                  : fieldHeader}
              </Heading>
              <Text variant="headlineM">
                {`${fieldSubheader} ${i18n('mktg-abtest-hph1-heading-4')}`}
              </Text>
              {fieldNineteenAction && fieldNineteenAction.entity && (
                <NineteenAction entity={fieldNineteenAction.entity} />
              )}
            </Box>
            {fieldVideoLink ? (
              <button
                css={(theme: LucidTheme) => ([{
                  alignSelf: 'flex-start',
                  background: 'transparent',
                  border: 0,
                  display: 'inline-block',
                  flex: '0 1 100%',
                  outlineColor: theme.colors.orange[30],
                  outlineWidth: '2px',
                  padding: 0,
                  position: 'relative',
                  cursor: 'pointer',
                  marginTop: theme.space[4],
                  [mqMin.tablet]: {
                    flex: '0 1 42%',
                    marginTop: 0
                  }
                },
                isEnded && {
                  '&::after': {
                    animation: `${nineteenHeroPlayButton} 0.3s ease`,
                    background: theme.colors.white,
                    borderRadius: '50%',
                    bottom: '2rem',
                    boxShadow: theme.shadows.listItem,
                    color: theme.colors.indigo[60],
                    content: '"▶"',
                    fontSize: '1.5rem',
                    height: '3rem',
                    lineHeight: '2',
                    paddingLeft: '4px',
                    position: 'absolute',
                    right: '2rem',
                    transition: 'transform 0.2s ease',
                    width: '3rem'
                  },
                  '&:hover': {
                    '&::after': {
                      transform: 'scale(1.2)'
                    }
                  }
                }])}
                onClick={() => video!.getVideoModal(fieldVideoLink, fieldAutoplay || undefined)}
                type="button"
              >
                {getImageHtml()}
              </button>
            ) :
                (
                  <Box
                    css={(theme: LucidTheme) => ({
                      marginTop: theme.space[4],
                      [mqMin.tablet]: {
                        marginTop: theme.space[0]
                      }
                    })}
                    flex={{ default: '0 1 100%', tablet: '0 1 42%' }}
                  >
                    {getImageHtml()}
                  </Box>
                )}
          </Container>
        </Box>
      </ABTestBranch>
      <ABTestBranch branch={Variation.B}>
        <Box
          as="header"
          css={(theme: LucidTheme) => ({
            background: theme.colors.white,
            position: 'relative'
          })}
          py={{ default: 5, tablet: 7 }}
        >
          <Container displayFlex css={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box
              flex={{ default: '0 1 100%', tablet: '0 1 50%' }}
            >
              <Heading
                as="h1"
                fontSize={langPrefix === 'en' ? '10' : '9'}
                maxWidth={{ default: '100%', mobile: '18rem', tablet: '33rem' }}
                mb={{ default: 3, tablet: 4 }}
                variant="headline3XL"
              >
                The visual workspace for remote teams
              </Heading>
              <Text variant="headlineM">
                Accomplish more by collaborating in real time. Accelerate
                understanding and drive innovation with powerful diagramming,
                whiteboarding, and data visualization. Sign up for a free trial
                today.
              </Text>
              {fieldNineteenAction && fieldNineteenAction.entity && (
                <NineteenAction entity={fieldNineteenAction.entity} />
              )}
            </Box>
            {fieldVideoLink ? (
              <button
                css={(theme: LucidTheme) => ([{
                  alignSelf: 'flex-start',
                  background: 'transparent',
                  border: 0,
                  display: 'inline-block',
                  flex: '0 1 100%',
                  outlineColor: theme.colors.orange[30],
                  outlineWidth: '2px',
                  padding: 0,
                  position: 'relative',
                  cursor: 'pointer',
                  marginTop: theme.space[4],
                  [mqMin.tablet]: {
                    flex: '0 1 42%',
                    marginTop: 0
                  }
                },
                isEnded && {
                  '&::after': {
                    animation: `${nineteenHeroPlayButton} 0.3s ease`,
                    background: theme.colors.white,
                    borderRadius: '50%',
                    bottom: '2rem',
                    boxShadow: theme.shadows.listItem,
                    color: theme.colors.indigo[60],
                    content: '"▶"',
                    fontSize: '1.5rem',
                    height: '3rem',
                    lineHeight: '2',
                    paddingLeft: '4px',
                    position: 'absolute',
                    right: '2rem',
                    transition: 'transform 0.2s ease',
                    width: '3rem'
                  },
                  '&:hover': {
                    '&::after': {
                      transform: 'scale(1.2)'
                    }
                  }
                }])}
                onClick={() => video!.getVideoModal(fieldVideoLink, fieldAutoplay || undefined)}
                type="button"
              >
                {getImageHtml()}
              </button>
            ) :
                (
                  <Box flex={{ default: '0 1 100%', tablet: ' 0 1 42%' }}>
                    <RemoteWorkImage />
                  </Box>
                )}
          </Container>
        </Box>
      </ABTestBranch>
    </ABTest>
  );
};

export default NineteenHeroImage;

export const query = graphql`
  fragment nineteenHeroImage on Drupal_ParagraphNineteenHeroImage {
    fieldHeader
    fieldHeaderPiece
    fieldSubheader
    fieldImage
    fieldVideoInitialLoop
    fieldVideoLink
    fieldAutoplay
    fieldNineteenAction {
      entity {
        ...nineteenAction
      }
    }
  }
`;
